import { CopyOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import cn from 'classnames';
import { useCallback } from 'react';

import type { ModelActionChatMessage } from '@app/api/ActionsHistory';
import { Avatar } from '@app/components/Avatar';
import { useLeadCardContext } from '@app/components/LeadCard/context';
import type { LeadCardHistoryItemData } from '@app/components/LeadCard/data';
import { LeadAudioMessage } from '@app/components/LeadCard/ui/MessageHistory/components/LeadAudioMessage';
import { LeadCallMessage } from '@app/components/LeadCard/ui/MessageHistory/components/LeadCallMessage';
import { LeadCommentMessage } from '@app/components/LeadCard/ui/MessageHistory/components/LeadCommentMessage';
import { LeadCompletedTaskMessage } from '@app/components/LeadCard/ui/MessageHistory/components/LeadCompletedTaskMessage';
import { LeadFileAttachment } from '@app/components/LeadCard/ui/MessageHistory/components/LeadFileAttachment';
import { LeadFileHistoryAction } from '@app/components/LeadCard/ui/MessageHistory/components/LeadFileHistoryAction';
import { LeadImageAttachment } from '@app/components/LeadCard/ui/MessageHistory/components/LeadImageAttachment';
import { LeadLocationMessage } from '@app/components/LeadCard/ui/MessageHistory/components/LeadLocationMessage';
import { LeadTechnicalMessage } from '@app/components/LeadCard/ui/MessageHistory/components/LeadTechnicalMessage';
import { LeadTextMessage } from '@app/components/LeadCard/ui/MessageHistory/components/LeadTextMessage';
import { LeadVideoAttachment } from '@app/components/LeadCard/ui/MessageHistory/components/LeadVideoAttachment';
import { MessageSendErrorAction } from '@app/components/LeadCard/ui/MessageHistory/components/MessageSendErrorAction';
import {
  LEAD_MSG_STATUS_DELETED,
  LEAD_MSG_STATUS_ERROR,
  isCompletedTaskHistoryItem,
  isLeadAudioMessageType,
  isLeadCallMessageType,
} from '@app/components/LeadCard/ui/MessageHistory/lib';
import { CHAT_MESSAGE } from '@app/constants';
import {
  CHANGE_CLIENT_FILES,
  CHANGE_CLIENT_FILES_ADD,
  CHANGE_CLIENT_FILES_DELETE,
} from '@app/constants/clientsHistoryActionTypes';
import { SMS_SERVICE, WHATSAPP } from '@app/constants/socialIntegrationsTypes';
import { useNewUserAccessRights } from '@app/hooks/useNewUserAccessRights';
import { useTechSupportMode } from '@app/providers/TechSupportMode/useTechSupportMode';
import { SocialIntegrationIcon } from '@app/shared/ui/SocialIntegrationIcon';
import { transformImportedName } from '@app/utils';

import type { FC } from 'react';

import './LeadMessageItem.scss';

const MASKED_ACCOUNT = '*************';
const SEND_IN = 'Диалог ведёт c';
const SEND_OUT = 'Отправлено в';
const CLIENT_FILE_ACTION_IDS = new Set([
  CHANGE_CLIENT_FILES,
  CHANGE_CLIENT_FILES_ADD,
  CHANGE_CLIENT_FILES_DELETE,
]);

interface LeadMessageItemProps {
  item: LeadCardHistoryItemData;
  // Подряд идущие сообщения одного отправителя показывают шапку у первого, а
  // подпись — у последнего. Значения по умолчанию дают прежний вид: каждое
  // сообщение само себе группа.
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
  isReadPending?: boolean;
  onReadElement?(
    message: ModelActionChatMessage,
    element: HTMLDivElement | null
  ): void;
}

export const LeadMessageItem: FC<LeadMessageItemProps> = ({
  item,
  isFirstInGroup = true,
  isLastInGroup = true,
  isReadPending = false,
  onReadElement,
}) => {
  const { leadCardData, copyMessageInfo, isLoadingCopyMessageInfo } =
    useLeadCardContext();
  const { enabled: isTechSupportModeEnabled } = useTechSupportMode();
  const {
    userAccessRights: {
      Clients: { IsShowContacts },
    },
  } = useNewUserAccessRights();

  const chatMessage = item.ChatMessages?.[0] ?? null;
  const readElementHandler = useCallback(
    (element: HTMLDivElement | null) => {
      if (chatMessage) onReadElement?.(chatMessage, element);
    },
    [chatMessage, onReadElement]
  );

  if (item.ActionId !== CHAT_MESSAGE) {
    if (item.ActionId && CLIENT_FILE_ACTION_IDS.has(item.ActionId)) {
      return <LeadFileHistoryAction item={item} />;
    }

    if (item.ObjectType === 'ClientComment') {
      return <LeadCommentMessage item={item} />;
    }

    if (isCompletedTaskHistoryItem(item)) {
      return <LeadCompletedTaskMessage item={item} />;
    }

    return <LeadTechnicalMessage item={item} />;
  }

  if (!chatMessage) return null;

  const isIncoming = chatMessage.Direction === 'in';
  const recommend = chatMessage.RecommendChat;
  const messageStatus = recommend
    ? recommend.recipientState === 'failed'
      ? LEAD_MSG_STATUS_ERROR
      : recommend.recipientState
    : chatMessage.Status;
  const authorLabel =
    recommend?.origin === 'system'
      ? 'Уведомление'
      : recommend?.origin === 'sendout'
      ? 'Рассылка'
      : 'Оператор';
  const isWhatsapp = chatMessage.SocialType === WHATSAPP;
  const isSmsService = chatMessage.SocialType === SMS_SERVICE;
  const accountSign = isWhatsapp || isSmsService ? '+' : '';

  const clientPhoto = leadCardData?.ClientView.Photo ?? null;
  const clientNameFirst = leadCardData?.ClientView.NameFirst ?? null;
  const clientNameLast = leadCardData?.ClientView.NameLast ?? null;

  const avatarSrc = isIncoming ? clientPhoto : chatMessage.UserPhoto;
  const avatarNameFirst = transformImportedName(
    isIncoming ? clientNameFirst : chatMessage.UserFirstName
  );
  const avatarNameLast = transformImportedName(
    isIncoming ? clientNameLast : chatMessage.UserLastName
  );

  const operatorFullName =
    recommend?.author.displayName ||
    `${chatMessage.UserLastName ?? ''} ${
      chatMessage.UserFirstName ?? ''
    }`.trim();
  const integrationWithSender = `${chatMessage.SocialIntegrationName ?? ''} ${
    chatMessage.SenderAccountLogin ?? ''
  }`.trim();
  const headerName = isIncoming ? operatorFullName : integrationWithSender;

  const socialTypeLabel = chatMessage.SocialTypeUserFriendly ?? '';
  const maskedSenderAccount = IsShowContacts
    ? chatMessage.SenderAccountLogin ?? ''
    : MASKED_ACCOUNT;
  const maskedReceiverAccount = IsShowContacts
    ? chatMessage.ReceiverAccountLogin ?? ''
    : MASKED_ACCOUNT;

  const incomingMetaParts = [
    socialTypeLabel,
    `${accountSign}${maskedSenderAccount}`,
  ].filter(Boolean);
  const incomingMeta = incomingMetaParts.join(' ');

  const incomingFooterParts = [
    socialTypeLabel,
    `${accountSign}${chatMessage.ReceiverAccountLogin ?? ''}`,
  ].filter(Boolean);
  const incomingFooter = incomingFooterParts.join(' ');

  const outgoingFooterParts = [
    socialTypeLabel,
    `${accountSign}${maskedReceiverAccount}`,
  ].filter(Boolean);
  const outgoingFooter = outgoingFooterParts.join(' ');

  const isDeleted = messageStatus === LEAD_MSG_STATUS_DELETED;
  const isError = messageStatus === LEAD_MSG_STATUS_ERROR;
  const hasReaction = !!chatMessage.ReactionEmojiCompanion;
  const showActions = (!isIncoming && isError) || hasReaction;

  const replyMessage =
    chatMessage.ReplyClientMessageId !== null
      ? chatMessage.ReplyClientMessage
      : null;

  const sharedBubbleProps = {
    socialType: chatMessage.SocialType,
    isIncoming,
    timestamp: chatMessage.Timestamp,
    wasEdited: chatMessage.WasEdited,
    status: messageStatus,
    isForwarded: chatMessage.IsForwarded,
    replyMessage,
    replyMessageId: chatMessage.ReplyClientMessageId,
  };

  const imageExtensions = new Set(['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp']);
  const videoExtensions = new Set(['mp4']);
  const audioExtensions = new Set(['mp3', 'ogg', 'wav', 'amr', 'opus']);
  const documentExtensions = new Set([
    'doc',
    'docx',
    'pdf',
    'txt',
    'xls',
    'xlsx',
  ]);
  const chatFile = chatMessage.File;
  const attachmentMessage =
    chatMessage.Message || chatFile?.Description || null;
  const isCallMessage = isLeadCallMessageType(chatMessage.Type);

  const isAudioMessage = (() => {
    const type = chatMessage.Type;
    if (isCallMessage) return false;
    if (isLeadAudioMessageType(type)) return true;
    if (!chatFile) return false;
    const ext = (chatFile.Extension ?? '').trim().toLowerCase();
    return audioExtensions.has(ext);
  })();

  const isImageFile = (() => {
    if (isAudioMessage) return false;
    if (!chatFile) return false;
    const ext = (chatFile.Extension ?? '').trim().toLowerCase();
    if (ext === 'webp' && chatFile.ExternalAccessId) return false;
    return imageExtensions.has(ext);
  })();

  const isVideoFile = (() => {
    if (isAudioMessage) return false;
    if (!chatFile) return false;
    const ext = (chatFile.Extension ?? '').trim().toLowerCase();
    if (ext === 'webp' && chatFile.ExternalAccessId) return true;
    return videoExtensions.has(ext);
  })();

  const isDocumentFile = (() => {
    if (isAudioMessage || isImageFile || isVideoFile || isCallMessage) {
      return false;
    }
    if (!chatFile) return false;
    const ext = (chatFile.Extension ?? '').trim().toLowerCase();
    return documentExtensions.has(ext);
  })();

  const isUnknownAttachment =
    !!chatFile &&
    !isCallMessage &&
    !isAudioMessage &&
    !isImageFile &&
    !isVideoFile &&
    !isDocumentFile;

  const bubble = isCallMessage ? (
    <LeadCallMessage
      messageId={chatMessage.MessageId}
      file={chatFile}
      message={chatMessage.Message}
      transcription={chatMessage.Transcription}
      messageType={chatMessage.Type}
      grade={chatMessage.Grade}
      aiRecommendations={chatMessage.AiRecommendations}
      {...sharedBubbleProps}
    />
  ) : isAudioMessage ? (
    <LeadAudioMessage
      messageId={chatMessage.MessageId}
      file={chatFile}
      messageType={chatMessage.Type}
      transcription={chatMessage.Transcription}
      grade={chatMessage.Grade}
      aiRecommendations={chatMessage.AiRecommendations}
      {...sharedBubbleProps}
    />
  ) : isImageFile && chatFile ? (
    <LeadImageAttachment
      file={chatFile}
      message={attachmentMessage}
      {...sharedBubbleProps}
    />
  ) : isVideoFile && chatFile ? (
    <LeadVideoAttachment
      file={chatFile}
      message={attachmentMessage}
      {...sharedBubbleProps}
    />
  ) : (isDocumentFile || isUnknownAttachment) && chatFile ? (
    <LeadFileAttachment
      file={chatFile}
      message={chatMessage.Message}
      {...sharedBubbleProps}
    />
  ) : chatMessage.LocationLatLon ? (
    <LeadLocationMessage
      locationLatLon={chatMessage.LocationLatLon}
      locationName={chatMessage.LocationName}
      locationAddress={chatMessage.LocationAddress}
      message={chatMessage.Message}
      {...sharedBubbleProps}
    />
  ) : chatMessage.Message ? (
    <LeadTextMessage message={chatMessage.Message} {...sharedBubbleProps} />
  ) : null;

  return (
    <div
      className={cn('LeadMessageItem', {
        'LeadMessageItem--out': !isIncoming,
      })}
      data-message-id={chatMessage.MessageId}
    >
      {/* Колонка аватара остаётся всегда: без неё пузыри сообщений группы
          съехали бы к краю. */}
      <div className="LeadMessageItem__avatar">
        {isFirstInGroup && (
          <Avatar
            src={avatarSrc}
            nameFirst={avatarNameFirst}
            nameLast={avatarNameLast}
            size={36}
          />
        )}
      </div>
      <div className="LeadMessageItem__body">
        {isFirstInGroup && (
          <div className="LeadMessageItem__header">
            <SocialIntegrationIcon type={chatMessage.SocialType} size="sm" />
            <span className="LeadMessageItem__header-name">{headerName}</span>
          </div>
        )}
        {(isFirstInGroup || (isIncoming && isDeleted)) && (
          <div className="LeadMessageItem__meta">
            {isIncoming ? (
              <>
                {isFirstInGroup && <span>{incomingMeta}</span>}
                {isDeleted && (
                  <span className="LeadMessageItem__deleted-notice">
                    клиент удалил сообщение
                  </span>
                )}
              </>
            ) : (
              <>
                <span>
                  {`${authorLabel}: `}
                  {operatorFullName}
                </span>
                {chatMessage.BranchName && (
                  <span className="LeadMessageItem__branch">
                    {chatMessage.BranchName}
                  </span>
                )}
              </>
            )}
          </div>
        )}
        <div className="LeadMessageItem__content">
          {isTechSupportModeEnabled && (
            <Tooltip title="Копировать информацию о сообщении">
              <span>
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => copyMessageInfo(chatMessage.MessageId)}
                  loading={isLoadingCopyMessageInfo}
                  disabled={isLoadingCopyMessageInfo}
                  size="small"
                  type="text"
                  className={cn('copy-message-info-button', {
                    'copy-message-info-button--incoming': isIncoming,
                  })}
                />
              </span>
            </Tooltip>
          )}
          {recommend && bubble ? (
            <div
              className="LeadMessageItem__recommend-bubble"
              ref={readElementHandler}
            >
              {bubble}
            </div>
          ) : (
            bubble
          )}
          {showActions && (
            <div className="LeadMessageItem__actions">
              {!isIncoming && isError && (
                <MessageSendErrorAction
                  clientMessageId={chatMessage.MessageId}
                  errorMessage={chatMessage.ErrorMessage}
                  integrationId={chatMessage.SocialIntegrationId}
                />
              )}
              {hasReaction && (
                <div className="LeadMessageItem__reaction">
                  {chatMessage.ReactionEmojiCompanion}
                </div>
              )}
            </div>
          )}
        </div>
        {isIncoming &&
          recommend &&
          (isReadPending || recommend.staffReadPending) && (
            <span className="LeadMessageItem__read-pending">
              Прочтение подтверждается
            </span>
          )}
        {isLastInGroup && (
          <div className="LeadMessageItem__footer">
            {isIncoming ? (
              <>
                <span>
                  {isCallMessage ? 'На номер' : SEND_IN} {incomingFooter}
                </span>
                {chatMessage.BranchName && (
                  <span className="LeadMessageItem__branch">
                    {chatMessage.BranchName}
                  </span>
                )}
              </>
            ) : (
              <span>
                {isCallMessage ? 'Звонок на' : SEND_OUT} {outgoingFooter}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
